import { useEffect, useMemo, useState } from 'react';
import { apis, hooks, models } from "@vote4dance/judging-sdk";
import { StreamSource } from '@vote4dance/judging-sdk/dist/hooks/streams';
var ActiveCompetitionSource = models.competitions.ActiveCompetitionSource;
var EventPageSource = models.competitions.EventPageSource;
var Stream = hooks.streams.Stream;
var Api = apis.Api;
import { CompetitionStatus } from "@vote4dance/judging-schema";
function FederationEventPageSource(federation_id, range, offset, size) {
    return new StreamSource(`/federations/${federation_id}/event-page/${range}/${offset}/${size}`);
}
const PAGE_SIZE = 20;
const mergeCompetitionLists = (...lists) => {
    const seen = new Set();
    const merged = [];
    for (const list of lists) {
        list === null || list === void 0 ? void 0 : list.forEach(item => {
            const id = item === null || item === void 0 ? void 0 : item.competition_id;
            if (id) {
                if (seen.has(id))
                    return;
                seen.add(id);
            }
            if (item)
                merged.push(item);
        });
    }
    return merged;
};
const filterNewCompetitions = (existing, incoming) => {
    if (incoming.length === 0)
        return [];
    const seen = new Set();
    existing.forEach(item => { if (item.competition_id)
        seen.add(item.competition_id); });
    const filtered = [];
    incoming.forEach(item => {
        if (!item)
            return;
        const id = item.competition_id;
        if (id) {
            if (seen.has(id))
                return;
            seen.add(id);
        }
        filtered.push(item);
    });
    return filtered;
};
export function usePaginatedCompetitions(rangeFilter, enabled = true, fallbackPageFull = false, federation_id) {
    const source = useMemo(() => {
        if (!enabled)
            return undefined;
        return federation_id
            ? FederationEventPageSource(federation_id, rangeFilter, 0, PAGE_SIZE)
            : EventPageSource(rangeFilter, 0, PAGE_SIZE);
    }, [enabled, rangeFilter, federation_id]);
    const [page, loading] = Stream(source);
    const [additionalResults, setAdditionalResults] = useState([]);
    const [offset, setOffset] = useState(PAGE_SIZE);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMoreFromServer, setHasMoreFromServer] = useState(false);
    const pageCompetitions = useMemo(() => mergeCompetitionLists(page === null || page === void 0 ? void 0 : page.competitions), [page === null || page === void 0 ? void 0 : page.competitions]);
    const combinedCompetitions = useMemo(() => mergeCompetitionLists(pageCompetitions, additionalResults), [pageCompetitions, additionalResults]);
    useEffect(() => {
        if (!page) {
            setHasMoreFromServer(false);
            return;
        }
        const baseLength = pageCompetitions.length;
        const pageIsFull = baseLength >= PAGE_SIZE;
        const hasMoreByCount = typeof page.count === 'number' && page.count > baseLength;
        setHasMoreFromServer(hasMoreByCount || (fallbackPageFull && pageIsFull));
    }, [fallbackPageFull, page, pageCompetitions.length]);
    useEffect(() => {
        if (enabled)
            return;
        setAdditionalResults([]);
        setOffset(PAGE_SIZE);
        setLoadingMore(false);
        setHasMoreFromServer(false);
    }, [enabled]);
    useEffect(() => {
        setAdditionalResults([]);
        setOffset(PAGE_SIZE);
        setLoadingMore(false);
        setHasMoreFromServer(false);
    }, [rangeFilter, federation_id]);
    const loadMore = async () => {
        if (!enabled || loadingMore || !hasMoreFromServer)
            return;
        setLoadingMore(true);
        try {
            const src = federation_id
                ? FederationEventPageSource(federation_id, rangeFilter, offset, PAGE_SIZE)
                : EventPageSource(rangeFilter, offset, PAGE_SIZE);
            const nextPage = await Api.read(src.url);
            const newCompetitions = mergeCompetitionLists(nextPage === null || nextPage === void 0 ? void 0 : nextPage.competitions);
            const uniqueNew = filterNewCompetitions(combinedCompetitions, newCompetitions);
            const combinedLength = combinedCompetitions.length + uniqueNew.length;
            if (uniqueNew.length > 0)
                setAdditionalResults(prev => [...prev, ...uniqueNew]);
            const nextHasMore = typeof (nextPage === null || nextPage === void 0 ? void 0 : nextPage.count) === 'number'
                ? nextPage.count > combinedLength
                : fallbackPageFull && uniqueNew.length === PAGE_SIZE;
            setHasMoreFromServer(nextHasMore);
            setOffset(prev => prev + PAGE_SIZE);
        }
        finally {
            setLoadingMore(false);
        }
    };
    return { combined: combinedCompetitions, loading, loadingMore, hasMore: hasMoreFromServer, loadMore };
}
const { useStreamResource } = hooks.streamResources;
export function useCompetition(competition_id) {
    const [allCompetitions, loading, error] = Stream(competition_id ? ActiveCompetitionSource(competition_id) : undefined);
    const derived = deriveCompetitionState(allCompetitions, competition_id);
    return [
        derived.inputCompetition,
        derived.event,
        derived.competitions,
        loading,
        error,
        derived.canEdit,
    ];
}
const deriveCompetitionState = (allCompetitions, competition_id) => {
    const event = allCompetitions && allCompetitions[0];
    const competitions = allCompetitions ? allCompetitions.filter((event) => event.competition_parent_id != null && parseInt(event.competition_parent_id) > 0) : [];
    const inputCompetition = allCompetitions && allCompetitions.find((event) => event.competition_id == competition_id);
    const canEdit = allCompetitions ? allCompetitions.some(a => ![CompetitionStatus.Closed, CompetitionStatus.Archived].includes(a.competition_status)) : false;
    return {
        inputCompetition,
        event,
        competitions,
        canEdit,
    };
};
export const useCompetitionResource = (competition_id) => {
    const source = useMemo(() => competition_id ? ActiveCompetitionSource(competition_id) : undefined, [competition_id]);
    return useStreamResource(source);
};
export const useCompetitionSuspense = (competition_id) => {
    if (!competition_id) {
        return {
            inputCompetition: undefined,
            event: undefined,
            competitions: undefined,
            canEdit: false,
        };
    }
    const resource = useCompetitionResource(competition_id);
    if (!resource) {
        return {
            inputCompetition: undefined,
            event: undefined,
            competitions: undefined,
            canEdit: false,
        };
    }
    const allCompetitions = resource.read();
    return deriveCompetitionState(allCompetitions, competition_id);
};
